import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@core/app-config/app-config.service';
import { MybConfig } from '@core/app-config/types/myb-config';
import { CookieStorageService } from '@core/index';
import { LanguageService } from '@core/language/language.service';
import { WindowService } from '@core/window-actions';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  EmailQualifierRequestBody,
  PnrRequest,
} from '../../manage-flights.type';

@Injectable({
  providedIn: 'root',
})
export class ManageFlightsApiService {
  mybConfig: MybConfig;

  constructor(
    private httpClient: HttpClient,
    public appConfigService: AppConfigService,
    private windowService: WindowService,
    private languageService: LanguageService,
    private cookieStorageService: CookieStorageService,
  ) {
    this.mybConfig = this.appConfigService.mybConfig;
  }

  generateOpenToken(accessToken: string, email: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: this.appConfigService.mybConfig.ssoAPIKey,
    });

    const requestBody = {
      jbSSOGenerateTokenServiceRQ: {
        accessToken,
        loginID: email,
      },
    };

    return this.httpClient.post(
      `${this.mybConfig.ssoUrl}/generateOpenToken`,
      requestBody,
      {
        headers,
      },
    );
  }

  validateOpenToken(accessToken: string, sabreOpenToken: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      apikey: this.appConfigService.mybConfig.ssoAPIKey,
    });

    const requestBody = {
      jbSSOValidateTokenServiceRQ: {
        accessToken,
        sabreOpenToken,
      },
    };

    return this.httpClient.post(
      `${this.mybConfig.ssoUrl}/validateOpenToken`,
      requestBody,
      {
        headers,
      },
    );
  }

  mybPnrService(requestBody: PnrRequest): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'app-id': this.appConfigService.mybConfig.pnrServiceAppId,
      apikey: this.appConfigService.mybConfig.pnrServiceApiKey,
    });

    return this.httpClient
      .post(this.mybConfig.pnrServiceUrl, requestBody, {
        headers,
      })
      .pipe(
        catchError(errorRes => {
          return throwError(() => errorRes);
        }),
      );
  }

  mybPnrEmailQualifierService(
    requestBody: EmailQualifierRequestBody,
  ): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'app-id': this.appConfigService.mybConfig.pnrServiceAppId,
      apikey: this.appConfigService.mybConfig.pnrServiceApiKey,
    });

    return this.httpClient
      .post(this.mybConfig.pnrEmailLocatorUrl, requestBody, {
        headers,
      })
      .pipe(
        catchError(errorRes => {
          return throwError(() => {
            if (errorRes.status === 0) {
              // failed to connect to server
              return {
                error: {
                  status: errorRes.status,
                  code: 'Connection problem',
                  httpStatus: errorRes.statusText,
                },
              };
            }
            return errorRes.error || errorRes;
          });
        }),
      );
  }

  doHealthCheck(b6PNRRecordLocator: string, lastName: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'app-id': this.appConfigService.mybConfig.pnrServiceAppId,
      apikey: this.appConfigService.mybConfig.pnrServiceApiKey,
    });

    const requestBody = {
      PNRHealthCheckRQ: {
        b6PNRRecordLocator,
        lastName,
      },
    };

    return this.httpClient.post(
      `${this.mybConfig.healthCheckServiceUrl}`,
      requestBody,
      {
        headers,
      },
    );
  }

  generateSabreDxUrl(
    lastName: string,
    pnrRecordLocator: string,
    queryParams: any,
  ) {
    const locale = queryParams.locale || this.languageService.getLocale();
    const platform = queryParams.un_jtt_application_platform;
    let source;
    if (!!platform) {
      if (this.windowService.checkMobileOperatingSystem() === 'iOS') {
        source = 'imobile';
      } else if (
        this.windowService.checkMobileOperatingSystem() === 'Android'
      ) {
        source = 'amobile';
      }
    }

    let redirectUrl = `${this.appConfigService.mybConfig.sabreDXUrl}?lastName=${lastName}&pnr=${pnrRecordLocator}&locale=${locale}`;
    if (source) {
      redirectUrl = `${redirectUrl}&source=${source}`;
    }
    return redirectUrl;
  }

  /** Externally navigate to sabre DX */
  redirectToSabarDx(
    lastName: string,
    pnrRecordLocator: string,
    queryParams: any,
  ) {
    const redirectUrl = this.generateSabreDxUrl(
      lastName,
      pnrRecordLocator,
      queryParams,
    );
    this.windowService.navigateToOutsideUrl(redirectUrl, true);
  }

  /** Internally navigate to email qualifier page */
  redirectToEmailQualifier(lastName: string, pnrRecordLocator: string) {
    this.windowService.internalNavigate(
      `${
        this.appConfigService.mybEmailUpdateURL
      }?locale=${this.languageService.getLocale()}#lastName=${lastName}&pnr=${pnrRecordLocator}`,
    );
  }

  validateSabreDomain(redirectUrl: string): boolean {
    const baseUrl = new URL(this.appConfigService.mybConfig.sabreDXUrl);
    return redirectUrl.indexOf(baseUrl.origin) === 0;
  }

  redirectToSabreAfterLogin() {
    let redirectUrl = this.cookieStorageService.getCookie(
      this.appConfigService.mybConfig.sabreReturnUrlCookieName,
    );

    if (redirectUrl && this.validateSabreDomain(redirectUrl)) {
      this.cookieStorageService.removeCookie(
        this.appConfigService.mybConfig.sabreReturnUrlCookieName,
      );
    } else {
      redirectUrl = `${
        this.appConfigService.mybConfig.sabreDXTripsUrl
      }&locale=${this.languageService.getLocale()}`;
    }
    this.windowService.navigateToOutsideUrl(redirectUrl, true);
  }
}
